import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetCoursesQuery } from '../redux/apiSlice';

const CourseList = () => {
  const [filter, setFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [coursesPerPage] = useState(10);
  const [hoveredCourse, setHoveredCourse] = useState(null);
  const navigate = useNavigate();

  // Use the get courses query with pagination and filtering
  const { 
    data: response, 
    isLoading, 
    isError, 
    error,
    refetch,
    isFetching
  } = useGetCoursesQuery({ 
    status: filter !== 'ALL' ? filter : undefined,
    page: currentPage, 
    limit: coursesPerPage 
  });

  // Extract data from response
  const courses = response?.courses || [];
  const pagination = response?.pagination || {
    total: 0,
    page: 1,
    limit: coursesPerPage,
    totalPages: 0
  };

  // Filter courses based on search term
  const filteredCourses = useMemo(() => {
    if (!searchTerm.trim()) return courses;
    
    return courses.filter(course => {
      const title = course.title?.toLowerCase() || '';
      const description = course.description?.toLowerCase() || '';
      const searchLower = searchTerm.toLowerCase();
      
      return title.includes(searchLower) || description.includes(searchLower);
    });
  }, [courses, searchTerm]);

  // Get filter counts from the response
  const filters = useMemo(() => {
    // If we have the data, calculate counts from all courses
    return [
      { 
        id: 'ALL', 
        name: 'All Courses', 
        count: pagination.total || 0
      },
      { 
        id: 'PUBLISHED', 
        name: 'Published', 
        count: courses.filter(c => c.status === 'PUBLISHED').length 
      },
      { 
        id: 'DRAFT', 
        name: 'Draft', 
        count: courses.filter(c => c.status === 'DRAFT').length 
      },
      { 
        id: 'PENDING', 
        name: 'Pending', 
        count: courses.filter(c => c.status === 'PENDING').length 
      }
    ];
  }, [courses, pagination.total]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'PUBLISHED': return 'bg-green-100 text-green-800 border-green-200';
      case 'DRAFT': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'PENDING': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PUBLISHED': return '🟢';
      case 'DRAFT': return '🟡';
      case 'PENDING': return '🔵';
      default: return '⚪';
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === 0) return 'Free';
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('PKR', 'Rs.');
  };

  const getSafeImage = (course) => {
    if (course?.thumbnailUrl) {
      return course.thumbnailUrl;
    }
    // Default course image
    return 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80';
  };
   
  const handleViewCourse = (courseId) => {
    navigate(`/courses/coursedetail/${courseId}`);
  };

  const handleCreateCourse = () => {
    navigate('/courses/create');
  };

  const handleEditCourse = (courseId) => {
    navigate(`/courses/create?edit=${courseId}`);
  };

  const calculateDraftProgress = (course) => {
    let completedFields = 0;
    const totalFields = 9;
    
    if (course.title) completedFields++;
    if (course.description) completedFields++;
    if (course.price !== null && course.price !== undefined) completedFields++;
    if (course.thumbnailUrl) completedFields++;
    if (course.categoryId) completedFields++;
    if (course.subcategoryId) completedFields++;
    if (course.language) completedFields++;
    if (course.subtitle) completedFields++;
    if (course.totalDurationHours > 0) completedFields++;
    
    return Math.round((completedFields / totalFields) * 100);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate total students from all courses using backend data
  const totalStudents = courses.reduce((sum, course) => {
    return sum + (course.totalStudents || 0);
  }, 0);

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-300 hover:scale-110 ${
            currentPage === i
              ? 'bg-green-500 text-white shadow-md'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="flex justify-center items-center space-x-2 mt-8 animate-fade-in">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-300 hover:scale-110 ${
            currentPage === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Previous
        </button>

        {pages}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === pagination.totalPages}
          className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-300 hover:scale-110 ${
            currentPage === pagination.totalPages
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Next
        </button>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12 animate-pulse">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
            <p className="mt-4 text-gray-600 animate-fade-in">Loading courses...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12 animate-fade-in">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error loading courses</h3>
            <p className="text-gray-600 mb-4">{error?.data?.message || 'Please try again later'}</p>
            <button 
              onClick={refetch}
              className="px-6 py-3 bg-green-500 text-white rounded-xl font-semibold transition-all duration-300 hover:bg-green-600 hover:scale-105"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50 py-4 sm:py-6 md:py-8 px-3 sm:px-4 md:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header with Create Button */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 md:mb-12">
          <div className="text-center sm:text-left mb-4 sm:mb-0 animate-fade-in">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 animate-slide-down">
              My Courses
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl animate-slide-up">
              Manage, edit, and track performance of all your courses
            </p>
          </div>
          
          <button 
            onClick={handleCreateCourse}
            className="sm:self-start px-4 sm:px-6 py-2 sm:py-3 bg-green-500 text-white rounded-xl md:rounded-2xl font-semibold transition-all duration-300 hover:bg-green-600 hover:scale-105 hover:rotate-1 flex items-center justify-center space-x-2 shadow-lg text-sm sm:text-base w-full sm:w-auto animate-fade-in"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Create Course</span>
          </button>
        </div>

        {/* Stats and Search Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Total Courses Card */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 border-l-4 border-green-500 transform transition-all duration-500 hover:scale-105 hover:shadow-xl animate-fade-in hover:rotate-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Total Courses</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{pagination.total}</p>
              </div>
              <div className="p-2 sm:p-3 rounded-lg sm:rounded-xl bg-green-50 text-green-600 transform transition-all duration-300 hover:scale-110 hover:rotate-12">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
            </div>
          </div>

          {/* Total Students Card */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 border-l-4 border-purple-500 transform transition-all duration-500 hover:scale-105 hover:shadow-xl animate-fade-in-up hover:-rotate-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Total Students</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{totalStudents}</p>
              </div>
              <div className="p-2 sm:p-3 rounded-lg sm:rounded-xl bg-purple-50 text-purple-600 transform transition-all duration-300 hover:scale-110 hover:-rotate-12">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 border-l-4 border-blue-500 transform transition-all duration-500 hover:scale-105 hover:shadow-xl animate-fade-in">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base hover:shadow-md"
              />
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8 transform transition-all duration-500 hover:shadow-xl animate-fade-in-up">
          <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center justify-between sm:hidden">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium text-sm transition-all duration-300 hover:bg-gray-200 hover:scale-105"
              >
                <svg className="w-4 h-4 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
                </svg>
                <span>Filters</span>
              </button>
            </div>

            <div className="hidden sm:flex flex-wrap gap-2 lg:gap-3">
              {filters.map((filterItem, index) => (
                <button
                  key={filterItem.id}
                  onClick={() => {
                    setFilter(filterItem.id);
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-2 lg:px-4 lg:py-2 rounded-lg lg:rounded-xl text-sm font-medium whitespace-nowrap transition-all duration-300 transform hover:scale-105 hover:shadow-md ${
                    filter === filterItem.id
                      ? 'bg-green-500 text-white shadow-md lg:shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } animate-fade-in`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {filterItem.name} ({filterItem.count})
                </button>
              ))}
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 sm:hidden animate-slide-down">
              <div className="grid grid-cols-2 gap-2">
                {filters.map((filterItem) => (
                  <button
                    key={filterItem.id}
                    onClick={() => {
                      setFilter(filterItem.id);
                      setCurrentPage(1);
                      setShowFilters(false);
                    }}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 transform hover:scale-105 ${
                      filter === filterItem.id
                        ? 'bg-green-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {filterItem.name} ({filterItem.count})
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {filteredCourses.map((course, index) => (
            <div
              key={course.id}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden transform transition-all duration-500 hover:scale-105 hover:shadow-2xl group animate-fade-in"
              style={{ animationDelay: `${index * 100}ms` }}
              onMouseEnter={() => setHoveredCourse(course.id)}
              onMouseLeave={() => setHoveredCourse(null)}
            >
              <div className="relative overflow-hidden">
                <img
                  src={getSafeImage(course)}
                  alt={course.title || 'Course'}
                  className="w-full h-40 sm:h-44 md:h-48 object-cover transform transition-all duration-700 group-hover:scale-110"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80';
                  }}
                />
                <div className={`absolute top-2 sm:top-3 md:top-4 right-2 sm:right-3 md:right-4 transition-all duration-500 ${hoveredCourse === course.id ? 'scale-110 rotate-3' : ''}`}>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(course.status)} transition-all duration-300 group-hover:scale-110`}>
                    <span className="hidden sm:inline">{getStatusIcon(course.status)} </span>
                    {course.status}
                  </span>
                </div>
                <div className={`absolute bottom-2 sm:bottom-3 md:bottom-4 left-2 sm:left-3 md:left-4 transition-all duration-500 ${hoveredCourse === course.id ? 'scale-110 -rotate-3' : ''}`}>
                  <span className="px-2 py-1 bg-black bg-opacity-70 text-white rounded text-xs sm:text-sm font-semibold transform transition-all duration-300 group-hover:scale-110">
                    {formatCurrency(course.price)}
                  </span>
                </div>
                
                {course.status === 'DRAFT' && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-2 transform transition-all duration-500 translate-y-full group-hover:translate-y-0">
                    <div className="w-full bg-gray-600 rounded-full h-2 overflow-hidden">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-1000 ease-out" 
                        style={{ 
                          width: `${calculateDraftProgress(course)}%`,
                          transform: hoveredCourse === course.id ? 'scaleX(1.05)' : 'scaleX(1)'
                        }}
                      ></div>
                    </div>
                    <p className="text-white text-xs text-center mt-1 animate-pulse">
                      {calculateDraftProgress(course)}% Complete
                    </p>
                  </div>
                )}
              </div>

              <div className="p-4 sm:p-5 md:p-6">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded transform transition-all duration-300 group-hover:scale-105 group-hover:bg-green-100">
                    Category {course.categoryId || 'N/A'}
                  </span>
                  <div className="flex items-center space-x-1 text-yellow-500 transform transition-all duration-300 group-hover:scale-105 group-hover:rotate-12">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="text-xs sm:text-sm font-semibold text-gray-700">New</span>
                  </div>
                </div>

                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-green-600 transition-colors duration-300 transform group-hover:translate-x-1">
                  {course.title || 'Untitled Course'}
                </h3>
                <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2 transition-all duration-300 group-hover:text-gray-900">
                  {course.description || 'No description available'}
                </p>

                <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 mb-3 sm:mb-4 text-center">
                  <div className="transform transition-all duration-300 group-hover:scale-105 group-hover:text-green-600">
                    <div className="text-sm sm:text-base md:text-lg font-bold text-gray-900">
                      {course.totalStudents || 0}
                    </div>
                    <div className="text-xs text-gray-500">Students</div>
                  </div>
                  <div className="transform transition-all duration-300 group-hover:scale-105 group-hover:text-green-600">
                    <div className="text-sm sm:text-base md:text-lg font-bold text-gray-900">
                      {course.totalVideos || 0}
                    </div>
                    <div className="text-xs text-gray-500">Videos</div>
                  </div>
                  <div className="transform transition-all duration-300 group-hover:scale-105 group-hover:text-green-600">
                    <div className="text-sm sm:text-base md:text-lg font-bold text-gray-900">
                      {course.totalSections || 0}
                    </div>
                    <div className="text-xs text-gray-500">Sections</div>
                  </div>
                </div>

                <div className="flex space-x-2 sm:space-x-3">
                  <button 
                    onClick={() => handleEditCourse(course.id)}
                    className="flex-1 px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 hover:bg-green-600 hover:scale-105 hover:shadow-lg flex items-center justify-center space-x-1 group"
                  >
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Edit</span>
                  </button>
                  <button 
                    onClick={() => handleViewCourse(course.id)}
                    className="flex-1 px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 hover:bg-gray-200 hover:scale-105 hover:shadow-lg flex items-center justify-center space-x-1 group"
                  >
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span>View</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {renderPagination()}

        {/* Empty State */}
        {filteredCourses.length === 0 && !isLoading && (
          <div className="text-center py-12 sm:py-16 animate-fade-in">
            <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto mb-4 bg-gray-100 rounded-xl sm:rounded-2xl flex items-center justify-center transform transition-all duration-500 hover:scale-110 hover:rotate-6">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-gray-400 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No courses found</h3>
            <p className="text-gray-600 text-sm sm:text-base mb-4 sm:mb-6 px-4">Try adjusting your search or filter criteria</p>
            <button 
              onClick={handleCreateCourse}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-green-500 text-white rounded-xl sm:rounded-2xl font-semibold transition-all duration-300 hover:bg-green-600 hover:scale-105 hover:shadow-xl text-sm sm:text-base animate-pulse"
            >
              Create Your First Course
            </button>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        @keyframes fade-in-up {
          from { 
            opacity: 0; 
            transform: translateY(30px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out both;
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out both;
        }
        .animate-slide-down {
          animation: slide-down 0.5s ease-out both;
        }
        .animate-slide-up {
          animation: slide-up 0.5s ease-out both;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default CourseList;